import { RangeErrorType } from '@admin-panel/utils/ranges.types';
import { Pipe, PipeTransform } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { pluralize } from '@common/utils/plural';
import { ErrorCodeInterface } from '@shared/types/error-code.interface';

/**
 * Maps Angular validation error keys (and backend {@link ErrorCodeInterface}s)
 * to human-readable, Polish error messages.
 */
export const ERROR_MAP: Record<string, string | ((errorValue: any) => string)> = {
  //! form errors
  required: `To pole jest wymagane`,
  minlength: (errorData: { requiredLength: number }) =>
    `Wpisz co najmniej ${errorData.requiredLength} ${pluralize(errorData.requiredLength, 'znak', 'znaki', 'znaków')}`,
  maxlength: (errorData: { requiredLength: number; actualLength: number }) =>
    `Przekroczono limit znaków (${errorData.actualLength}/${errorData.requiredLength})`,
  min: (errorData: { min: number }) => `Wartość musi być większa lub równa ${errorData.min}`,
  max: (errorData: { max: number }) => `Wartość musi być mniejsza lub równa ${errorData.max}`,
  email: `Wpisz poprawny adres email`,
  pattern: (errorData: { requiredPattern: string }) => {
    if (errorData.requiredPattern === '^\\d{11}$') {
      return `PESEL musi składać się z 11 cyfr`;
    }
    if (errorData.requiredPattern.includes('http')) {
      return `Wklej poprawny link`;
    }
    if (errorData.requiredPattern.includes('[a-zA-Z') || errorData.requiredPattern.includes('ąćęłńóśźż')) {
      return `Pole może zawierać tylko litery`;
    }
    return `Nieprawidłowy format`;
  },
  roomCategory: 'Wypełnij wszystkie pola',
  invalidAgeRange: (errorData: { minAge: number; maxAge: number; name: string }) =>
    `Wiek od (${errorData.minAge}) nie może być większy niż wiek do (${errorData.maxAge}) dla kategorii „${errorData.name}”`,
  endDateRequired: 'Wybierz pełny zakres dat',
  outOfPartyDates: 'Daty muszą pokrywać się z datami imprezy',
  invalidPhone: 'Wpisz poprawny numer telefonu',
  invalidEmail: 'Wpisz poprawny adres email',
  nip: 'Wpisz poprawny numer NIP',
  adultAtDeparture: 'Osoba musi mieć ukończone 18 lat w dniu wyjazdu',
  adultMaxAge: (errorData: { actual: number }) =>
    `Czy na pewno masz ${errorData.actual} ${pluralize(errorData.actual, 'rok', 'lata', 'lat')}?`,
  ageOutOfRangeTraining: (errorData: { min: number; max: number }) =>
    `Dziecko musi mieć między ${errorData.min} a ${errorData.max} lat w dniu rozpoczęcia szkolenia`,
  postalCode: 'Kod pocztowy powinien wyglądać następująco: 00-000 i składać się z cyfr',
  invalidBuildingNumber: 'Numer domu powinien składać się z cyfr i opcjonalnie litery',
  invalidApartmentNumber: 'Numer lokalu powinien składać się z cyfr i opcjonalnie litery',
  ageRangeError: (errorData: {
    type: RangeErrorType;
    value1: number | string;
    name1?: string;
    value2: number | string;
    name2?: string;
  }) =>
    errorData.type === RangeErrorType.Gap
      ? `Występuje dziura między zakresami wieku (między ${errorData.value2} a ${errorData.value1}). Popraw ją, aby zapisać`
      : `Niektóre zakresy wieku na siebie nachodzą (${errorData.name2} do ${errorData.value2}, ${errorData.name1} od ${errorData.value1}). Popraw je, aby zapisać`,
  ageRangeEndGapError: (errorData: { value: number | string }) =>
    `Występuje dziura od ${errorData.value} lat do końca zakresu. Zakres musi być wypełniony do końca, aby zapisać`,
  skiPassDuplicates: 'Nazwy skipasów muszą być unikalne',
  firstSkipassRequired: 'Pierwszy skipass musi mieć nazwę',
  requiredAtLeastOneHotel: 'Dodaj przynajmniej jeden hotel i wypełnij jego dane',
  reservationTooFewRooms: 'Wybrano zbyt mało pokoi, aby pomieścić wszystkich uczestników',
  reservationTooManyRooms: 'Wybrano zbyt wiele pokoi w stosunku do liczby uczestników',
  minMaxEqual: 'Dla apartamentu maksymalna ilość osób musi być większa niż minimalna',
  minPeopleCountMaxBed: 'Minimalna ilość osób musi być mniejsza lub równa maksymalnej ilości łóżek',
  depositNotBeforeMain: 'Data zaliczki musi być wcześniejsza niż data całkowitej płatności',
  depositAmountNotPositive: 'Kwota zaliczki musi być większa niż 0',
  depositAmountNotBelowTotal: 'Kwota zaliczki musi być niższa niż całkowita cena',
  depositPairingRequired: 'Kwota i data zaliczki są wymagane',
  paymentDateInPast: 'Data płatności nie może być w przeszłości',
  paymentDateAfterEventStart: 'Data płatności nie może być późniejsza niż data rozpoczęcia imprezy',
  [ErrorCodeInterface.UnknownError]: `Wystąpił nieznany błąd. Spróbuj ponownie później.`,
};

@Pipe({
  name: 'mapError',
  pure: true,
})
/**
 * Pipe that converts validation errors into localized user-facing messages.
 *
 * Supports both Angular {@link ValidationErrors} objects and string error codes.
 */
export class MapErrorPipe implements PipeTransform {
  /**
   * Maps form control errors to a single message string.
   *
   * @param ngControlErrors - Validation errors object or string error code.
   * @returns Localized error message or `null` when there is no error.
   */
  transform(ngControlErrors: ValidationErrors | null | undefined | string): string | null {
    if (!ngControlErrors) {
      return null;
    }

    if (typeof ngControlErrors === 'string') {
      const errorValue = ERROR_MAP[ngControlErrors];
      return (
        (typeof errorValue === 'function' ? errorValue({}) : errorValue) ||
        (ERROR_MAP[ErrorCodeInterface.UnknownError] as string)
      );
    }

    if (typeof ngControlErrors === 'object' && Object.keys(ngControlErrors).length === 0) {
      return null;
    }

    for (const errorType in ERROR_MAP) {
      if (errorType in ngControlErrors) {
        const valueOrFn = ERROR_MAP[errorType];
        if (typeof valueOrFn === 'function') {
          return valueOrFn(ngControlErrors[errorType]) as string;
        } else {
          return valueOrFn as string;
        }
      }
    }
    return JSON.stringify(ngControlErrors);
  }
}
